import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import ManageAgentsDialog from "@/components/ManageAgentsDialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UserPlus, ArrowLeft } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

/**
 * v1.27.90 — Portal side of party-managed agents.
 * The party account holder (role='customer') opens this from Settings and
 * gets the same dialog the Company Admin sees, just scoped to their own
 * party via _require_agent_manager on the backend.
 */
export default function PortalAgents() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [agents, setAgents] = useState([]);

    const partyStub = user?.party_id ? { id: user.party_id, name: user?.party_name || "My Account" } : null;

    const load = async () => {
        try {
            const { data } = await api.get("/portal/my-agents");
            setAgents(Array.isArray(data) ? data : (data.agents || []));
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    if (!partyStub) {
        return <div className="p-6 text-sm text-slate-600">Only party account holders can manage agents.</div>;
    }

    return (
        <div className="space-y-4 p-4" data-testid="portal-agents-page">
            <div className="flex items-center justify-between">
                <div>
                    <button onClick={() => navigate(-1)} className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                        <ArrowLeft size={12} /> Back
                    </button>
                    <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">My Agents</h1>
                    <p className="mt-1 text-sm text-slate-500">Add people who can upload slips under your account. Each agent logs in with their own email or mobile.</p>
                </div>
                <Button data-testid="open-agents-dialog" onClick={() => setOpen(true)} className="bg-indigo-600 text-white hover:bg-indigo-700">
                    <UserPlus size={14} className="mr-1.5" /> Add Agent
                </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-600">{agents.length} agent{agents.length === 1 ? "" : "s"} linked</div>
                {agents.length === 0 ? (
                    <div className="rounded border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                        No agents yet. Tap <b>Add Agent</b> above to invite one.
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {agents.map((a) => (
                            <li key={a.id} data-testid={`portal-agent-row-${a.id}`} className="flex items-center justify-between gap-3 py-2">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-900">{a.name}</span>
                                        {a.active === false && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-rose-700">Deactivated</span>}
                                    </div>
                                    <div className="text-xs text-slate-500">{a.email || "—"} · {a.phone || "—"}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <ManageAgentsDialog
                open={open}
                onOpenChange={(v) => { setOpen(v); if (!v) load(); }}
                party={partyStub}
            />
        </div>
    );
}
